import { AnalyticsData } from '@monoimin/hooks';
import { Locales } from '@monoimin/sb-types';
import { getAnalyticsTrackingId } from '@monoimin/utils/analytics';
import { getDeviceInfo } from 'libs/analytics/src/lib/amplitude';
import { createCountryInfo } from 'libs/analytics/src/lib/utils/createCountryInfo';
import { generateEventId } from 'libs/analytics/src/lib/utils/generateEventId';
import { FormZbFields } from 'libs/forms/src/gravity-form/types';
import { getSessionStartTime } from 'libs/utils/src/lib/storage/sessionStorage';

type AttributeMapping = {
  source: 'values' | 'payload';
  key: string;
};

const ATTRIBUTE_MAPPINGS: Record<string, AttributeMapping> = {
  user_name: { source: 'payload', key: 'user_name' },
  user_lastname: { source: 'payload', key: 'user_Lastname' },
  user_email: { source: 'payload', key: 'user_email' },
  user_phone: { source: 'payload', key: 'user_phone' },
  user_agent: { source: 'payload', key: 'user_agent' },
  user_cookie_consent: { source: 'values', key: 'user_cookie_consent' },
  pr_spouse: { source: 'values', key: 'pr_spouse' },
  pr_children: { source: 'values', key: 'pr_children' },
  pr_partners: { source: 'values', key: 'pr_partners' },
  partner_comment: { source: 'values', key: 'partner_comment' },
  user_parents_flag: { source: 'values', key: 'pr_parents' },
  user_siblings_flag: { source: 'values', key: 'pr_siblings' },
  user_daughters_flag: { source: 'values', key: 'pr_daughters' },
  user_sons_flag: { source: 'values', key: 'pr_sons' },
  user_questions: { source: 'values', key: 'user_questions' },
  sf_lead_country: { source: 'payload', key: 'sf_lead_country' },
  sf_lead_f_status: { source: 'payload', key: 'sf_lead_f_status' },
  page_type: { source: 'payload', key: 'sf_lead_f_type' },
  pr_fs_goal: { source: 'values', key: 'pr_fs_goal' },
  user_city: { source: 'payload', key: 'ipcity' },
  ipcountry: { source: 'payload', key: 'ipcountry' },
  // Device data
  da_device_vendor: { source: 'payload', key: 'da_device_vendor' },
  da_device_model: { source: 'payload', key: 'da_device_model' },
  da_marketing_name: { source: 'payload', key: 'da_marketing_name' },
  da_year_released: { source: 'payload', key: 'da_year_released' },
  fr_device_type: { source: 'payload', key: 'device_type' },
  fr_platform: { source: 'payload', key: 'platform' },
  // Form data
  program_name: { source: 'payload', key: 'fr_program_name' },
  fr_program_country: { source: 'payload', key: 'fr_program_country' },
  form_name: { source: 'payload', key: 'fr_form_title' },
  form_id: { source: 'payload', key: 'fr_form_id' },
  form_user_client_segment: {
    source: 'values',
    key: 'form_user_client_segment',
  },
  fr_date_created: { source: 'payload', key: 'fr_date_created' },
  fr_ip: { source: 'payload', key: 'fr_ip' },
  page_url: { source: 'payload', key: 'fr_source_url' },
  domain: { source: 'payload', key: 'domain' },
  // ZeroBounce data
  zb_account: { source: 'payload', key: 'zb_account' },
  zb_domain: { source: 'payload', key: 'zb_domain' },
  zb_free_email: { source: 'payload', key: 'zb_free_email' },
  zb_smtp_provider: { source: 'payload', key: 'zb_smtp_provider' },
  zb_status: { source: 'payload', key: 'zb_status' },
  zb_sub_status: { source: 'payload', key: 'zb_sub_status' },
  // Meeting data
  mtg_connection_type: { source: 'values', key: 'mtg_connection_type' },
  mtg_date_user: { source: 'values', key: 'mtg_date_user' },
  mtg_manager_name: { source: 'values', key: 'mtg_manager_name' },
  mtg_program: { source: 'values', key: 'mtg_program' },
  mtg_questions: { source: 'values', key: 'mtg_questions' },
  mtg_time_manager: { source: 'values', key: 'mtg_time_manager' },
  mtg_time_user: { source: 'values', key: 'mtg_time_user' },
  mtg_user_time_zone: { source: 'values', key: 'mtg_user_time_zone' },
  form_mtg_type: { source: 'values', key: 'mtg_type' },

  form_meeting_manager: { source: 'values', key: 'form_meeting_manager' },
  form_questions: { source: 'values', key: 'form_questions' },
  form_meeting_platform: { source: 'values', key: 'form_meeting_platform' },
  form_user_goal: { source: 'values', key: 'form_user_goal' },
  form_meeting_time: { source: 'values', key: 'form_meeting_time' },
  form_meeting_date: { source: 'values', key: 'form_meeting_date' },
  form_meeting_location: { source: 'values', key: 'form_meeting_location' },
  // KYC
  kyc_last_name_c: { source: 'values', key: 'kyc_last_name_c' },
  kyc_first_name_с: { source: 'values', key: 'kyc_first_name_с' },
  kyc_middle_name_c: { source: 'values', key: 'kyc_middle_name_c' },
  kyc_country__c: { source: 'values', key: 'kyc_country__c' },
  kyc_city__c: { source: 'values', key: 'kyc_city__c' },
  kyc_street__c: { source: 'values', key: 'kyc_street__c' },
  kyc_postalcode__c: { source: 'values', key: 'kyc_postalcode__c' },
  kyc_business_area_c: { source: 'values', key: 'kyc_business_area_c' },
  kyc_business_geography_c: {
    source: 'values',
    key: 'kyc_business_geography_c',
  },
  kyc_sources_of_wealth_c: { source: 'values', key: 'kyc_sources_of_wealth_c' },
  kyc_proof_of_income_with_tax_returns_c: {
    source: 'values',
    key: 'kyc_proof_of_income_with_tax_returns_c',
  },
  kyc_proof_of_income_comment_c: {
    source: 'values',
    key: 'kyc_proof_of_income_comment_c',
  },
  kyc_connections_with_sanctioned_countries_c: {
    source: 'values',
    key: 'kyc_connections_with_sanctioned_countries_c',
  },
  kyc_sanctioned_countries_comment_c: {
    source: 'values',
    key: 'kyc_sanctioned_countries_comment_c',
  },
  kyc_refusal_in_visa_residency_citizenship_c: {
    source: 'values',
    key: 'kyc_refusal_in_visa_residency_citizenship_c',
  },
  kyc_refusal_in_visa_comment_c: {
    source: 'values',
    key: 'kyc_refusal_in_visa_comment_c',
  },
  kyc_permit_permanent_res_citizenship_c: {
    source: 'values',
    key: 'kyc_permit_permanent_res_citizenship_c',
  },
  kyc_permit_permanent_res_citizenship_comment_c: {
    source: 'values',
    key: 'kyc_permit_permanent_res_citizenship_comment_c',
  },
  kyc_criminal_record_c: { source: 'values', key: 'kyc_criminal_record_c' },
  kyc_criminal_record_comment_c: {
    source: 'values',
    key: 'kyc_criminal_record_comment_c',
  },
  kyc_personal_bankruptcy_c: {
    source: 'values',
    key: 'kyc_personal_bankruptcy_c',
  },
  kyc_personal_bankruptcy_comment_c: {
    source: 'values',
    key: 'kyc_personal_bankruptcy_comment_c',
  },
  kyc_pep_c: { source: 'values', key: 'kyc_pep_c' },
  kyc_pep_comment_c: { source: 'values', key: 'kyc_pep_comment_c' },
  kyc_sanctions_c: { source: 'values', key: 'kyc_sanctions_c' },
  kyc_sanctions_comment_c: { source: 'values', key: 'kyc_sanctions_comment_c' },
  kyc_business_activities_of_dependents_c: {
    source: 'values',
    key: 'kyc_business_activities_of_dependents_c',
  },
  kyc_business_activities_comment_c: {
    source: 'values',
    key: 'kyc_business_activities_comment_c',
  },
  kyc_registered_marriage_with_spouse_c: {
    source: 'values',
    key: 'kyc_registered_marriage_with_spouse_c',
  },
  kyc_sponsor_c: { source: 'values', key: 'kyc_sponsor_c' },
  kyc_additional_comments_c: {
    source: 'values',
    key: 'kyc_additional_comments_c',
  },
  kyc_b2b_title__c: { source: 'values', key: 'kyc_b2b_title__c' },
  kyc_b2b_companyname__c: { source: 'values', key: 'kyc_b2b_companyname__c' },
  kyc_b2b_website__c: { source: 'values', key: 'kyc_b2b_website__c' },
  kyc_b2b_reg_number__c: { source: 'values', key: 'kyc_b2b_reg_number__c' },
  kyc_b2b_registrationdate__c: {
    source: 'values',
    key: 'kyc_b2b_registrationdate__c',
  },
  kyc_b2b_legaladdress__c: { source: 'values', key: 'kyc_b2b_legaladdress__c' },
  kyc_b2b_actualaddress__c: {
    source: 'values',
    key: 'kyc_b2b_actualaddress__c',
  },
  kyc_b2b_companyscope__c: { source: 'values', key: 'kyc_b2b_companyscope__c' },
  kyc_b2b_minimal_standard__c: {
    source: 'values',
    key: 'kyc_b2b_minimal_standard__c',
  },
  kyc_b2b_annual_financial_statements__c: {
    source: 'values',
    key: 'kyc_b2b_annual_financial_statements__c',
  },
  kyc_confirmation1: { source: 'values', key: 'kyc_confirmation1' },
  kyc_confirmation2: { source: 'values', key: 'kyc_confirmation2' },
  kyc_confirmation3: { source: 'values', key: 'kyc_confirmation3' },
  select_role_1: { source: 'values', key: 'select_role_1' },
  select_role_2: { source: 'values', key: 'select_role_2' },
  select_role_3: { source: 'values', key: 'select_role_3' },
  select_role_4: { source: 'values', key: 'select_role_4' },
  select_role_5: { source: 'values', key: 'select_role_5' },
  select_role_6: { source: 'values', key: 'select_role_6' },
  select_role_7: { source: 'values', key: 'select_role_7' },
  select_role_8: { source: 'values', key: 'select_role_8' },
  select_role_9: { source: 'values', key: 'select_role_9' },
  select_role_10: { source: 'values', key: 'select_role_10' },
  // survey
  answer_1_customer_survey: {
    source: 'values',
    key: 'answer_1_customer_survey',
  },
  answer_2_customer_survey: {
    source: 'values',
    key: 'answer_2_customer_survey',
  },
  answer_3_customer_survey: {
    source: 'values',
    key: 'answer_3_customer_survey',
  },
  answer_4_customer_survey: {
    source: 'values',
    key: 'answer_4_customer_survey',
  },
  answer_5_customer_survey: {
    source: 'values',
    key: 'answer_5_customer_survey',
  },
  answer_6_customer_survey: {
    source: 'values',
    key: 'answer_6_customer_survey',
  },
  comment_customer_survey: { source: 'values', key: 'comment_customer_survey' },
  language_url: { source: 'payload', key: 'language_form' },
  device_timezone: { source: 'payload', key: 'timezone' },
  sf_timezone: { source: 'payload', key: 'sf_timezone' },
};

export const mapAmplitudeAttributes = async (
  values: Record<string, any>,
  collectionDataPayload: Record<string, any>,
  validateEmailData?: FormZbFields | null,
  analyticsData?: AnalyticsData | null,
  locale?: Locales
) => {
  const mappedAttributes = Object.entries(ATTRIBUTE_MAPPINGS).reduce(
    (acc, [key, mapping]) => {
      // Не заполняем fr_program_name для mtg формы
      if (key === 'fr_program_name' && values['mtg_program']) {
        return acc;
      }

      const source =
        mapping.source === 'values' ? values : collectionDataPayload;
      let value =
        mapping.key === 'user_email_consent'
          ? values[mapping.key] === '1'
          : source[mapping.key];

      if (key === 'mtg_manager_name' && typeof value === 'string') {
        value = value
          .split('-')
          .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
          .join(' ');
      }

      return {
        ...acc,
        [key]: value,
      };
    },
    {}
  );

  const locationData: Record<string, string> = {};
  if (typeof window !== 'undefined') {
    Object.assign(locationData, {
      site_domain: window.location.hostname,
      site_hostname: window.location.hostname,
      page_location: window.location.href,
      page_path: window.location.pathname,
      page_url: window.location.href,
      page_title: document.title || '',
    });
  }

  const { region: programRegion } = await createCountryInfo({
    pageCountry: analyticsData?.pageCountry,
  });
  const { deviceId, sessionId } = getDeviceInfo();

  const trackingId = getAnalyticsTrackingId() || '';

  const currentDate = new Date();

  const additionalFields = {
    device_datetime: currentDate.toISOString(),
    program_status: analyticsData?.programStatus || '',
    page_language: locale,
    device_ga_cookie_id: trackingId,
    form_understand_price_flag: true,
    session_started_at: getSessionStartTime(),
    program_region: Array.isArray(programRegion)
      ? programRegion.join(',')
      : programRegion || '',
    program_flag: analyticsData?.programRelation || '',
    event_id: generateEventId('form', {
      formId: analyticsData?.formId || '',
      action: 'submit',
      timestamp: currentDate.toISOString(),
    }),
    event_date: currentDate.toISOString().split('T')[0],
    session_id: sessionId,
    device_id: deviceId,
    form_ui_element: analyticsData?.formUiElement || '',
    event_type: 'Form Submitted',
    form_type: analyticsData?.formType || '',
  };

  /* 
  Map these two fields when media/inquiry will work
  const press_topic_request 
  const press_comment 
  */

  // Incorporate ZeroBounce data if available
  if (validateEmailData) {
    return {
      ...mappedAttributes,
      ...locationData,
      ...additionalFields,
      zb_status: validateEmailData.zb_status,
      zb_sub_status: validateEmailData.zb_sub_status,
      zb_account: validateEmailData.zb_account,
      zb_domain: validateEmailData.zb_domain,
      zb_free_email: validateEmailData.zb_free_email,
      zb_smtp_provider: validateEmailData.zb_smtp_provider,
    };
  }

  return {
    ...mappedAttributes,
    ...locationData,
    ...additionalFields,
  };
};
